import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import { BaseInput } from '@components';
import { TEST_IDS } from '@constants/testIds.ts';
import { BodySmSmMedium } from '@constants/text.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import {
    getImportedLearningResults,
    updateImportedLearningsRequestData,
} from '@routes/profile-bar/___pages___/my-results/model/importedLearnings.slice.ts';
import { buildEditOrViewColumns } from '@routes/sidebar/admin-center/___shared___/buildEditOrViewColumns.tsx';
import { showNoSetValue } from '@routes/sidebar/admin-center/___shared___/components/tables/helpers.tsx';
import { CommonRegularTable } from '@routes/sidebar/admin-center/___shared___/components/tables/regular';
import { SearchInputWrapper } from '@routes/sidebar/admin-center/___shared___/styled.ts';
import { getBooleanTableFilters, mapFieldsToColumn } from '@routes/sidebar/admin-center/___shared___/utils.ts';
import { renderBuiltInStatusPill } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/helpers.tsx';
import { TableNoValidationMSGWrapper } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/styled.ts';
import type { ReferencesColumn } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { translateItem } from '@utils/translations.ts';
import type { ColumnsType, TableProps } from 'antd/es/table';

import { mapTableFiltersToRequestFilters } from '../my-learning/utils.tsx';
import { MY_IMPORTED_LEARNINGS_COLUMNS_VIEW } from './columns.ts';
import { MY_IMPORTED_LEARNINGS_COLUMN_FILTER_REQUEST_MAP, MY_IMPORTED_LEARNINGS_DATA_INDEX } from './constants.ts';
import type { FilterOption, ImportedByColumnData, MyImportedLearningRow } from './types.ts';
import { ImportedByCell, LearningTimeCell, mapUserImportedLearningToRow, toFilterOptions } from './utils.tsx';

export const SHARED_MY_IMPORTED_LEARNINGS_NO_IMPORTED_RESULTS = msg`No imported results available.`;

const {
    EMPLOYEE_ID,
    SOURCE_SYSTEM,
    SOURCE_USER_ID,
    TYPE,
    TITLE,
    COMPLETED_DATE,
    SOURCE_RANK,
    REQUIRED,
    COMPLETION,
    SCORE,
    LEARNING_TIME,
    IMPORTED_ON,
    IMPORTED_BY,
} = MY_IMPORTED_LEARNINGS_DATA_INDEX;

// TODO: Remove mocks when the table will be ready table
const MyImportedLearningsTable: React.FC = () => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const results = useAppSelector((state) => state.importedLearnings.myImportedLearningResults);
    const filterValues = results.resultsDescription;
    const requestData = useAppSelector((state) => state.importedLearnings.myImportedLearningsRequestData);
    const isPending = useAppSelector((state) => state.importedLearnings.isGetImportedLearningResultsPending);
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const { preferredDateFormat: dateFormat } = getUserPreferencesOrDefaults(userInfo);

    const [tableFilters, setTableFilters] = useState<Record<string, string[]> | null>(null);

    useEffect(() => {
        void dispatch(getImportedLearningResults(requestData));
    }, [dispatch, requestData]);

    const rows: MyImportedLearningRow[] = useMemo(() => results.data.map(mapUserImportedLearningToRow), [results.data]);

    const hasActiveFilters = Boolean(
        (requestData.textSearch?.trim() ?? '') !== '' ||
        (requestData.filters && Object.keys(requestData.filters).length > 0),
    );

    const viewCols = useMemo<ReferencesColumn[]>(
        () => MY_IMPORTED_LEARNINGS_COLUMNS_VIEW.map((field) => mapFieldsToColumn(field, t)),
        [t],
    );

    const baseColumns = useMemo(() => buildEditOrViewColumns<MyImportedLearningRow>(viewCols, () => false), [viewCols]);

    const filterOptionsByColumn = useMemo<Record<string, FilterOption[]>>(
        () => ({
            [EMPLOYEE_ID]: toFilterOptions(filterValues.employeeIds),
            [SOURCE_SYSTEM]: toFilterOptions(filterValues.sourceSystems),
            [SOURCE_USER_ID]: toFilterOptions(filterValues.sourceUserIds),
            [TYPE]: toFilterOptions(filterValues.types),
            [SOURCE_RANK]: toFilterOptions(filterValues.sourceRanks),
            [REQUIRED]: getBooleanTableFilters(),
            [IMPORTED_ON]: filterValues.importedOnDates.map((date) => ({
                text: formatDateBasedOnPreferences(date, dateFormat),
                value: date,
            })),
            [IMPORTED_BY]: toFilterOptions(filterValues.importedBy),
        }),
        [filterValues, dateFormat],
    );

    const columns: ColumnsType<MyImportedLearningRow> = useMemo(
        () =>
            baseColumns.map((col) => {
                const columnKey = String(col.key ?? '');
                let nextCol: any = col;

                if (columnKey === TITLE) {
                    nextCol = { ...col, render: (val: string) => <BodySmSmMedium>{val}</BodySmSmMedium> };
                }

                if (columnKey === COMPLETED_DATE || columnKey === IMPORTED_ON) {
                    nextCol = {
                        ...col,
                        render: (val: string | null) => formatDateBasedOnPreferences(val, dateFormat) || '-',
                    };
                }

                if (columnKey === SOURCE_RANK || columnKey === COMPLETION || columnKey === SCORE) {
                    nextCol = { ...col, render: (val: string | number | null) => showNoSetValue(val) };
                }

                if (columnKey === REQUIRED) {
                    nextCol = renderBuiltInStatusPill<MyImportedLearningRow>(col);
                }

                if (columnKey === LEARNING_TIME) {
                    nextCol = { ...col, render: (val: number | null) => <LearningTimeCell minutes={val} /> };
                }

                if (columnKey === IMPORTED_BY) {
                    nextCol = { ...col, render: (val: ImportedByColumnData) => <ImportedByCell {...val} /> };
                }

                const options = filterOptionsByColumn[columnKey];
                if (Array.isArray(nextCol.filters) && options) {
                    nextCol = {
                        ...nextCol,
                        filters: options,
                        filteredValue: tableFilters?.[columnKey] ?? null,
                        filterSearch: true,
                    };
                }

                return nextCol;
            }),
        [baseColumns, filterOptionsByColumn, tableFilters, dateFormat],
    );

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(
            updateImportedLearningsRequestData({
                ...requestData,
                textSearch: event.target.value,
                pageNumber: 1,
            }),
        );
    };

    const handleTableChange: TableProps<MyImportedLearningRow>['onChange'] = (_pagination, filters, _sorter, extra) => {
        // Pagination is handled by onPaginationChange; the table has no sorting.
        if (extra.action !== 'filter') {
            return;
        }

        const nextTableFilters = Object.entries(filters).reduce<Record<string, string[]>>((acc, [key, values]) => {
            const selected = (values ?? []).map(String);
            if (selected.length) {
                acc[key] = selected;
            }
            return acc;
        }, {});
        const hasFilters = Object.keys(nextTableFilters).length > 0;

        setTableFilters(hasFilters ? nextTableFilters : null);
        dispatch(
            updateImportedLearningsRequestData({
                ...requestData,
                filters: mapTableFiltersToRequestFilters(
                    hasFilters ? nextTableFilters : null,
                    MY_IMPORTED_LEARNINGS_COLUMN_FILTER_REQUEST_MAP,
                ),
                pageNumber: 1,
            }),
        );
    };

    const handlePaginationChange = (pageNumber: number, pageSize: number) => {
        dispatch(updateImportedLearningsRequestData({ ...requestData, pageNumber, pageSize }));
    };

    return (
        <>
            <SearchInputWrapper>
                <BaseInput
                    value={requestData.textSearch ?? ''}
                    allowClear
                    placeholder={t`Search title, source ID`}
                    prefixIcon={<SearchIcon />}
                    onChange={handleSearch}
                    data-cy={TEST_IDS.PROFILE.IMPORTED_LEARNINGS_SEARCH}
                />
            </SearchInputWrapper>
            <TableNoValidationMSGWrapper>
                <CommonRegularTable<MyImportedLearningRow>
                    tableLayout="fixed"
                    rows={rows}
                    columns={columns}
                    loading={isPending}
                    emptyTitle={translateItem(SHARED_MY_IMPORTED_LEARNINGS_NO_IMPORTED_RESULTS)}
                    emptyDescription={t`Matched historical records will appear here after import.`}
                    hasActiveFilters={hasActiveFilters}
                    pagination={{
                        pageNumber: requestData.pageNumber ?? 1,
                        pageSize: requestData.pageSize ?? 10,
                        totalItems: results.pagination.totalCount,
                    }}
                    onPaginationChange={handlePaginationChange}
                    onTableChange={handleTableChange}
                    dataCy={TEST_IDS.PROFILE.IMPORTED_LEARNINGS_TABLE}
                />
            </TableNoValidationMSGWrapper>
        </>
    );
};

export default MyImportedLearningsTable;
